import { Grid, Card, TextField, Chip, Box, CircularProgress, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import DashboardNavbar from 'components/DashboardNavbar';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import { useEffect, useState } from 'react';
import { queryEvents } from 'services/event-system/query-events';

export default function EventSystem() {
  const [userId, setUserId] = useState('');
  const [eventName, setEventName] = useState('');
  const [platform, setPlatform] = useState('');
  const [fromTime, setFromTime] = useState('');
  const [toTime, setToTime] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Helper function to get local time in ISO format
  const getLocalISOString = (date) => {
    const offset = date.getTimezoneOffset();
    const localDate = new Date(date.getTime() - (offset * 60000));
    return localDate.toISOString();
  };

  // Example queries that users can click on
  const exampleQueries = [
    {
      label: 'All Events',
      params: {}
    },
    {
      label: 'Frontend Events',
      params: { platform: 'FE' }
    },
    {
      label: 'Backend Events',
      params: { platform: 'BE' }
    },
    {
      label: 'Unity Engine Events',
      params: { platform: 'UE' }
    },
    {
      label: 'Last Hour',
      params: { 
        fromTime: getLocalISOString(new Date(Date.now() - 3600000)),
        toTime: getLocalISOString(new Date())
      }
    },
    {
      label: 'Last 24 Hours',
      params: { 
        fromTime: getLocalISOString(new Date(Date.now() - 86400000)),
        toTime: getLocalISOString(new Date())
      }
    }
  ];

  async function executeQuery(params) {
    setLoading(true);
    setError(null);
    
    try {
      const res = await queryEvents(params);
      
      if (res?.status === 200 || res?.status === 201) {
        setResults(res?.data);
      } else {
        setError(res?.response?.data?.message || 'Failed to fetch events');
        setResults(null);
      }
    } catch (err) {
      setError('An error occurred while fetching events');
      setResults(null);
    } finally {
      setLoading(false);
    }
  }

  function handleExampleClick(exampleParams) {
    // Update form fields with example values
    setUserId(exampleParams.user_id || '');
    setEventName(exampleParams.event_name || '');
    setPlatform(exampleParams.platform || '');
    
    // Convert ISO strings to datetime-local format for display
    if (exampleParams.fromTime) {
      const date = new Date(exampleParams.fromTime);
      setFromTime(date.toISOString().slice(0, 16));
    } else {
      setFromTime('');
    }
    
    if (exampleParams.toTime) {
      const date = new Date(exampleParams.toTime);
      setToTime(date.toISOString().slice(0, 16));
    } else {
      setToTime('');
    }
    
    executeQuery(exampleParams);
  }

  function handleRunQuery() {
    const params = {};
    
    if (userId) params.user_id = userId;
    if (eventName) params.event_name = eventName;
    if (platform) params.platform = platform;
    if (fromTime) {
      // Convert datetime-local format to ISO string (preserving local time)
      params.fromTime = getLocalISOString(new Date(fromTime));
    }
    if (toTime) {
      // Convert datetime-local format to ISO string (preserving local time)
      params.toTime = getLocalISOString(new Date(toTime));
    }
    
    executeQuery(params);
  }

  function handleClearFilters() {
    setUserId('');
    setEventName('');
    setPlatform('');
    setFromTime('');
    setToTime('');
    executeQuery({});
  }

  // Load initial data on mount
  useEffect(() => {
    executeQuery({});
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          {/* Header */}
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant='h4' fontWeight='medium' mb={2}>
                  Event System Query
                </MDTypography>
                <MDTypography variant='body2' color='text' mb={3}>
                  Query and analyze system events. Use the example queries below or create your own custom queries.
                </MDTypography>

                {/* Example Queries */}
                <MDBox mb={3}>
                  <MDTypography variant='h6' fontWeight='medium' mb={2}>
                    Example Queries
                  </MDTypography>
                  <MDBox display='flex' flexWrap='wrap' gap={1}>
                    {exampleQueries.map((example, index) => (
                      <Chip
                        key={index}
                        label={example.label}
                        onClick={() => handleExampleClick(example.params)}
                        color='primary'
                        variant='outlined'
                        sx={{ cursor: 'pointer' }}
                      />
                    ))}
                  </MDBox>
                </MDBox>

                {/* Query Filters */}
                <MDBox mb={3}>
                  <MDTypography variant='h6' fontWeight='medium' mb={2}>
                    Query Filters
                  </MDTypography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label='User ID'
                        placeholder='UUID of the user'
                        value={userId}
                        onChange={(e) => setUserId(e.target.value)}
                        variant='outlined'
                        size='small'
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label='Event Name'
                        placeholder='e.g., user_login, game_start'
                        value={eventName}
                        onChange={(e) => setEventName(e.target.value)}
                        variant='outlined'
                        size='small'
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth size='small'>
                        <InputLabel>Platform Type</InputLabel>
                        <Select
                          value={platform}
                          label='Platform Type'
                          onChange={(e) => setPlatform(e.target.value)}
                        >
                          <MenuItem value=''>All</MenuItem>
                          <MenuItem value='FE'>Frontend</MenuItem>
                          <MenuItem value='BE'>Backend</MenuItem>
                          <MenuItem value='UE'>Unity Engine</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label='From Time'
                        type='datetime-local'
                        value={fromTime}
                        onChange={(e) => setFromTime(e.target.value)}
                        variant='outlined'
                        size='small'
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label='To Time'
                        type='datetime-local'
                        value={toTime}
                        onChange={(e) => setToTime(e.target.value)}
                        variant='outlined'
                        size='small'
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </Grid>
                </MDBox>

                {/* Action Buttons */}
                <MDBox display='flex' gap={2} alignItems='center'>
                  <MDButton
                    color='info'
                    onClick={handleRunQuery}
                    disabled={loading}
                    sx={{ minWidth: '120px' }}
                  >
                    {loading ? <CircularProgress size={20} color='inherit' /> : 'Run Query'}
                  </MDButton>
                  <MDButton
                    color='secondary'
                    variant='outlined'
                    onClick={handleClearFilters}
                    disabled={loading}
                  >
                    Clear Filters
                  </MDButton>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>

          {/* Results Section */}
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant='h6' fontWeight='medium' mb={2}>
                  Results
                </MDTypography>

                {loading && (
                  <MDBox display='flex' justifyContent='center' alignItems='center' py={5}>
                    <CircularProgress />
                  </MDBox>
                )}

                {error && (
                  <MDBox
                    p={2}
                    bgcolor='error.main'
                    borderRadius='8px'
                    sx={{ backgroundColor: '#ffebee' }}
                  >
                    <MDTypography variant='body2' color='error'>
                      {error}
                    </MDTypography>
                  </MDBox>
                )}

                {!loading && !error && results && (
                  <MDBox>
                    <MDBox mb={2} display='flex' justifyContent='space-between' alignItems='center'>
                      <MDTypography variant='body2' color='text'>
                        {Array.isArray(results) 
                          ? `Found ${results.length} event(s)` 
                          : results.total 
                            ? `Found ${results.total} event(s)`
                            : 'Results'}
                      </MDTypography>
                    </MDBox>
                    
                    <Box
                      sx={{
                        maxHeight: '600px',
                        overflow: 'auto',
                        backgroundColor: '#f5f5f5',
                        padding: 2,
                        borderRadius: '8px',
                        fontFamily: 'monospace',
                        fontSize: '14px'
                      }}
                    >
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {JSON.stringify(results, null, 2)}
                      </pre>
                    </Box>
                  </MDBox>
                )}

                {!loading && !error && !results && (
                  <MDBox
                    display='flex'
                    justifyContent='center'
                    alignItems='center'
                    py={5}
                    sx={{ opacity: 0.5 }}
                  >
                    <MDTypography variant='body2' color='text'>
                      No data available. Run a query to see results.
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

